<template>
  <div class="emoji-picker" :class="{ 'emoji-picker--inline': !!inline }" v-if="showPicker" @click.stop>
    <div ref="emojiItemsRef" class="emoji-items" @scroll.passive="handleEmojiScroll">
      <div
        v-for="cat in categories"
        :key="cat.key"
        class="emoji-category"
      >
        <div
          class="emoji-anchor"
          :data-key="cat.key"
          :ref="(el) => setAnchorRef(cat.key, el)"
        ></div>
        <div class="emoji-category-grid">
          <div
            v-for="emoji in cat.emojis"
            :key="cat.key + '-' + emoji"
            class="emoji-item"
            :class="{
              'emoji-problematic': isProblematicEmoji(emoji),
              'emoji-missing': !emoji || emoji.trim() === ''
            }"
            :data-emoji-problematic="isProblematicEmoji(emoji)"
            :aria-label="getEmojiLabel(emoji)"
            @click="selectEmoji(emoji)"
          >
            {{ emoji }}
          </div>
        </div>
      </div>
    </div>
    <div ref="emojiTabsRef" class="emoji-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="emoji-tab btn"
        :class="[tab.key === activeCategory ? 'btn-primary' : 'btn-secondary']"
        @pointerdown.stop.prevent="handleTabPointerDown(tab.key, $event)"
        @click.stop="handleTabClick(tab.key, $event)"
      >
        <i class="fa" :class="tab.iconClass"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onActivated, onMounted, onUnmounted, ref, watch } from 'vue';
import type { ComponentPublicInstance } from 'vue';

const props = defineProps<{
  showPicker: boolean;
  inline?: boolean;
}>();

const emit = defineEmits<{
  (e: 'select', emoji: string): void;
  (e: 'close'): void;
}>();

type EmojiCategoryKey =
  | 'smileys'
  | 'gestures'
  | 'hearts'
  | 'animals'
  | 'food'
  | 'activities'
  | 'travel'
  | 'objects'
  | 'symbols'
  | 'flags';

type EmojiCategory = {
  key: EmojiCategoryKey;
  iconClass: string;
  emojis: string[];
};

const fallbackCategories: EmojiCategory[] = [
  {
    key: 'smileys',
    iconClass: 'fa-smile',
    emojis: [
      '😀','😃','😄','😁','😆','😅','😂','🤣','😊','😇','🙂','🙃','😉','😌','😍','🥰','😘','😗','😙','😚',
      '😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','🤥',
      '😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🤧','🥵','🥶','🥴','😵','🤯','🥳','😎','🤓','🧐',
      '😕','😟','🙁','☹️','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😱','😖','😣','😞','😓','😩',
      '😫','🥱','😤','😡','😠','🤬','😈','👿','💀','☠️','💩','🤡','👻','👽','👾','🤖'
      ,'😺','😸','😹','😻','😼','😽','🙀','😿','😾','🤠','🥸','🤡','👹','👺','👁️‍🗨️'
      ,'😇','🤤','🤩','🥹','🫠','🫢','🫣','🫡','🤫','🤭','🫤','🫥','😶‍🌫️','😮‍💨'
      ,'😵‍💫','🤐','🫨','😴','🤒','🤕'
    ],
  },
  {
    key: 'gestures',
    iconClass: 'fa-thumbs-up',
    emojis: [
      '👍','👎','👌','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','👇','☝️','✋','🤚','🖐️','🖖','👋','🤏','✊','🤛','🤜',
      '👏','🙌','👐','🤲','🙏','🤝','💪','🦾','🦿','🦵','🦶','👂','👃','🧠','🫀','🫁','🦷','🦴','👀','👁️','👅','👄','💋'
      ,'🫶','🤌','🫰','🫳','🫴','🤜🏻','🤛🏻','👍🏻','👎🏻','👏🏻','🙏🏻','👋🏻'
      ,'👊','🤲🏻','👐🏻','🙌🏻','🤝🏻','💪🏻','🫵','🫵🏻'
    ],
  },
  {
    key: 'hearts',
    iconClass: 'fa-heart',
    emojis: [
      '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝',
      '💟','💌','💋','💍','💎','🌹','🥀','💐','💒'
      ,'🫀','🫶','💑','👩‍❤️‍👩','👨‍❤️‍👨','👩‍❤️‍👨','💏','👩‍❤️‍💋‍👩','👨‍❤️‍💋‍👨','👩‍❤️‍💋‍👨'
      ,'💜','🩷','🩵','🩶','🩷','💙','💚'
    ],
  },
  {
    key: 'animals',
    iconClass: 'fa-paw',
    emojis: [
      '🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐻‍❄️','🐨','🐯','🦁','🐮','🐷','🐸','🐵','🐔','🐧','🐦','🦉','🦄','🐝','🦋','🐞',
      '🐢','🐍','🦎','🐙','🦑','🦀','🐟','🐬','🐳','🦈','🐊','🦓','🦒','🐘','🦣','🦏','🦛'
      ,'🐺','🦝','🦡','🐗','🐴','🐎','🫎','🦬','🐑','🐐','🦙','🦌','🐕','🐩','🦮','🐕‍🦺','🐈','🐈‍⬛'
      ,'🪽','🦜','🦢','🦩','🦚','🦃','🦤','🐓','🪿','🐇','🦇','🐌','🪱','🦟','🪰','🪲','🪳','🕷️','🦂'
      ,'🦞','🦐','🦪','🐠','🐡','🦭','🐋','🐆','🐅'
    ],
  },
  {
    key: 'food',
    iconClass: 'fa-cutlery',
    emojis: [
      '🍏','🍎','🍐','🍊','🍋','🍌','🍉','🍇','🍓','🫐','🍒','🍑','🥭','🍍','🥥','🥝','🍅','🥑','🫒','🍆','🥔','🥕','🌽','🌶️','🫑',
      '🥒','🥬','🥦','🧄','🧅','🍄','🥜','🌰','🍞','🥐','🥖','🫓','🥨','🥯','🥞','🧇','🧀','🍖','🍗','🥩','🥓','🍔','🍟','🍕','🌭','🥪',
      '🌮','🌯','🥙','🧆','🥚','🍳','🥘','🍲','🥣','🥗','🍿','🧈','🧂','🥫','🍱','🍚','🍜','🍝','🍣','🍤','🍦','🍩','🍪','🎂','🍰','🧁','🍫','🍬','🍭'
      ,'🍧','🍨','🍡','🍢','🍘','🍥','🥮','🍮','🍯','☕','🫖','🍵','🧃','🥤','🧋','🍶','🍺','🍻','🥂','🍷','🥃','🍸','🍹','🧉'
      ,'🍽️','🥄','🍴','🥢','🔪','🫙','🥡','🥟','🥠','🥧','🧊'
    ],
  },
  {
    key: 'activities',
    iconClass: 'fa-futbol',
    emojis: [
      '⚽','🏀','🏈','⚾','🎾','🏐','🏉','🎱','🏓','🏸','🥅','🏒','🏑','🥍','🏏','⛳','🏹','🎣','🥊','🥋','🎽','🛹','🛼','⛷️','🏂','🏋️','🤼','🤸','⛹️','🏌️','🏄','🚣','🏊','🚴','🚵','🏇','🧘','🎮','🎲','🎯'
      ,'🪁','🎳','🎿','🛷','🥌','🧗','🤺','🏹','🏆','🥇','🥈','🥉','🏅','🎖️','🏵️'
      ,'🎨','🎭','🎬','🎤','🎧','🎼','🎹','🥁','🪘','🎷','🎺','🎸','🪕','🎻'
    ],
  },
  {
    key: 'travel',
    iconClass: 'fa-plane',
    emojis: [
      '🚗','🚕','🚙','🚌','🚎','🚓','🚑','🚒','🚐','🛻','🚚','🚛','🚜','🏍️','🛵','🚲','🛴','🚆','🚇','🚈','✈️','🛫','🛬','🚀','🛸','⛵','🚤','🛳️','⚓',
      '🏁','🗺️','🧭','🏕️','🏖️','🏜️','🏝️','🏟️','🏛️','🗽','🗼','🏰'
      ,'🚔','🚍','🚘','🚖','🚡','🚠','🚟','🚞','🚝','🚄','🚅','🚃','🚋','🚊','🚉'
      ,'🛶','🛟','⛴️','🛥️','🚢','🛩️','🛻','🚁','🚂','🚏','🗿','🗻','🏔️','⛰️','🌋'
      ,'🏙️','🌅','🌄','🌃','🌉','🗺️'
    ],
  },
  {
    key: 'objects',
    iconClass: 'fa-lightbulb',
    emojis: [
      '⌚','📱','💻','🖥️','🖨️','🎥','📷','📸','🎧','🎤','☎️','📺','💡','🔦','🕯️','🧯','🔧','🔨','🪛','🪚','🔩','⚙️','🧲','🔑','🗝️','💳','💰','🪙',
      '✉️','📩','📦','📌','📍','✂️','📎','🖊️','🖋️','✏️','📝','📚','📖'
      ,'📒','📔','📕','📗','📘','📙','🗂️','📁','📂','🗃️','🗄️','🗑️'
      ,'🧾','📇','🗒️','📋','📈','📉','📊','🧮','📌','🔒','🔓','🔏','🔐'
      ,'🪪','🧳','🎁','🪄','🧸','🪅','🪆','🔭','🔬','🧫','🧪','🧬'
    ],
  },
  {
    key: 'symbols',
    iconClass: 'fa-hashtag',
    emojis: [
      '✨','⭐','🌟','💫','🔥','💥','💢','💦','💨','🕳️','💤','✅','☑️','✔️','✖️','➕','➖','➗','➰','〰️','©️','®️','™️','🔞','❗','❓','‼️','⁉️','💯','🔔','🔕','🎵','🎶'
      ,'🔁','🔂','🔄','🔀','⏩','⏪','⏫','⏬','🔼','🔽','▶️','⏸️','⏹️','⏺️'
      ,'♻️','⚠️','🚫','⛔','🛑','🆗','🆒','🆕','🆙','🆓','🅿️','ℹ️'
      ,'🔣','🔤','🔡','🔠','🔟','🔢','🔴','🟠','🟡','🟢','🔵','🟣','⚫','⚪'
    ],
  },
  {
    key: 'flags',
    iconClass: 'fa-flag',
    emojis: [
      '🏳️','🏴','🏁','🚩','🏳️‍🌈','🏳️‍⚧️'
      ,'🇺🇦','🇺🇸','🇬🇧','🇨🇦','🇦🇺','🇩🇪','🇫🇷','🇪🇸','🇮🇹','🇵🇱','🇳🇱','🇧🇪','🇨🇭','🇦🇹','🇸🇪','🇳🇴','🇩🇰','🇫🇮'
      ,'🇯🇵','🇰🇷','🇨🇳','🇮🇳','🇧🇷','🇲🇽','🇦🇷','🇹🇷','🇮🇱','🇸🇦','🇦🇪','🇪🇬'
    ],
  },
];

const activeCategory = ref<EmojiCategoryKey>('smileys');

const categories = computed<EmojiCategory[]>(() => {
  const t = (window as any)?.__vueMessagesTranslations;
  const external = t?.emojiCategories;
  if (Array.isArray(external) && external.length) {
    // Check if it's the old format (array of emoji strings) or new format (array of category objects)
    if (typeof external[0] === 'string') {
      // Old format: simple array of emoji strings - wrap in a single category
      return [{
        key: 'smileys',
        iconClass: 'fa-smile',
        emojis: external as string[],
      }];
    }
    // New format: array of category objects
    return external
      .filter((c: any) => c && typeof c.key === 'string' && Array.isArray(c.emojis))
      .map((c: any) => ({
        key: c.key as EmojiCategoryKey,
        iconClass: typeof c.iconClass === 'string' && c.iconClass ? c.iconClass : 'fa-smile',
        emojis: c.emojis as string[],
      }));
  }
  return fallbackCategories;
});

const tabs = computed(() => categories.value.map(({ key, iconClass }) => ({ key, iconClass })));

const emojiItemsRef = ref<HTMLDivElement | null>(null);
const emojiTabsRef = ref<HTMLDivElement | null>(null);

const anchorRefs = ref<Partial<Record<EmojiCategoryKey, HTMLElement>>>({});
const setAnchorRef = (
  key: EmojiCategoryKey,
  el: Element | ComponentPublicInstance | null
) => {
  if (!el) return;
  const dom = (el instanceof HTMLElement)
    ? el
    : ((el as any).$el as HTMLElement | undefined);
  if (!dom) return;
  anchorRefs.value[key] = dom;
};

const scrollToCategory = (key: EmojiCategoryKey, behavior: ScrollBehavior = 'smooth') => {
  const container = emojiItemsRef.value;
  const anchor = anchorRefs.value[key];
  if (!container || !anchor) return;
  const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
  const top = Math.min(anchor.offsetTop, maxTop);
  container.scrollTo({ top, behavior });
};

const handleTabPointerDown = (key: EmojiCategoryKey, _event?: PointerEvent) => {
  activeCategory.value = key;
};

const handleTabClick = (key: EmojiCategoryKey, event?: MouseEvent) => {
  activeCategory.value = key;
  scrollToCategory(key);

  const target = event?.currentTarget;
  if (target instanceof HTMLButtonElement) {
    target.blur();
  }
};

const scrollActiveTabIntoView = (behavior: ScrollBehavior = 'smooth') => {
  const container = emojiTabsRef.value;
  if (!container) return;
  const tab = container.querySelector<HTMLElement>('.emoji-tab.btn-primary');
  if (!tab) return;

  tab.scrollIntoView({
    behavior,
    block: 'nearest',
    inline: 'center',
  });
};



let scrollDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const updateActiveFromScroll = () => {
  const container = emojiItemsRef.value;
  if (!container) return;

  const top = container.scrollTop;
  const list = categories.value;
  if (!list.length) return;

  let bestKey = list[0].key;
  let bestTop = -Infinity;
  const threshold = top + Math.min(50, container.clientHeight * 0.1);

  for (const c of list) {
    const a = anchorRefs.value[c.key];
    if (!a) continue;
    const y = a.offsetTop;
    if (y <= threshold && y > bestTop) {
      bestTop = y;
      bestKey = c.key;
    }
  }

  if (activeCategory.value !== bestKey) {
    activeCategory.value = bestKey;
    nextTick(() => {
      scrollActiveTabIntoView('auto');
    });
  }
};

const handleEmojiScroll = () => {
  if (scrollDebounceTimer) clearTimeout(scrollDebounceTimer);
  scrollDebounceTimer = setTimeout(() => {
    updateActiveFromScroll();
    scrollDebounceTimer = null;
  }, 16);
};


const PROBLEMATIC_EMOJI = new Set([
  '🫠', '🫢', '🫣', '🫤', '🫥', '🫦', '🫧', '🫨', '🫩', '🫪',
  '🫫', '🫬', '🫭', '🫮', '🫯', '🫰', '🫱', '🫲', '🫳', '🫴',
  '🫵', '🫶', '🫷', '🫸', '🫹', '🫺', '🫻', '🫼', '🫽', '🫾',
  '🫿', '🫀', '🫁', '🫂', '🫃', '🫄', '🫅', '🫆', '🫇', '🫈',
  '👩‍❤️‍👨', '👩‍❤️‍💋‍👨', '👨‍❤️‍👨', '👩‍❤️‍👩',
  '🏳️‍🌈', '🏳️‍⚧️', '🏴‍☠️',
  '🫶', '🫵', '🫹', '🫺', '🫻', '🫼'
]);

const isProblematicEmoji = (emoji: string): boolean => {
  return PROBLEMATIC_EMOJI.has(emoji) || 
         emoji.includes('\u200D') ||
         emoji.includes('\uFE0F');
};

const getEmojiLabel = (emoji: string): string => {
  const labels: Record<string, string> = {
    '😀': 'Grinning face',
    '😃': 'Grinning face with big eyes',
    '😄': 'Grinning face with smiling eyes',
    '😁': 'Beaming face with smiling eyes',
    '😆': 'Grinning squinting face',
    '😅': 'Grinning face with sweat',
    '😂': 'Face with tears of joy',
    '🤣': 'Rolling on the floor laughing',
    '😊': 'Smiling face with smiling eyes',
    '😇': 'Smiling face with halo',
    '🙂': 'Slightly smiling face',
    '😉': 'Winking face',
    '😌': 'Relieved face',
    '😍': 'Smiling face with heart-eyes',
    '🥰': 'Smiling face with hearts',
    '😘': 'Face blowing a kiss',
    '😗': 'Kissing face',
    '😙': 'Kissing face with smiling eyes',
    '😚': 'Kissing face with closed eyes',
    '😋': 'Face savoring food',
    '😛': 'Face with tongue',
    '😜': 'Winking face with tongue',
    '🤪': 'Zany face',
    '😝': 'Squinting face with tongue',
    '🤗': 'Hugging face',
    '🤭': 'Face with hand over mouth',
    '🤫': 'Shushing face',
    '🤔': 'Thinking face',
    '🤐': 'Zipper-mouth face',
    '🤨': 'Face with monocle',
    '😐': 'Neutral face',
    '😑': 'Expressionless face',
    '😶': 'Face without mouth',
    '😏': 'Smirking face',
    '😒': 'Unamused face',
    '🙄': 'Face with rolling eyes',
    '😬': 'Grimacing face',
    '😮': 'Face with open mouth',
    '😯': 'Hushed face',
    '😲': 'Astonished face',
    '😳': 'Flushed face',
    '🥺': 'Pleading face',
    '😦': 'Frowning face with open mouth',
    '😧': 'Anguished face',
    '😨': 'Fearful face',
    '😰': 'Anxious face with sweat',
    '😱': 'Face screaming in fear',
    '😖': 'Confounded face',
    '😣': 'Persevering face',
    '😞': 'Disappointed face',
    '😓': 'Downcast face with sweat',
    '😩': 'Weary face',
    '😫': 'Tired face',
    '😤': 'Face with steam from nose',
    '😡': 'Pouting face',
    '😠': 'Angry face',
    '🤬': 'Face with symbols on mouth',
    '😈': 'Smiling face with horns',
    '👿': 'Angry face with horns',
    '💀': 'Skull',
    '☠️': 'Skull and crossbones',
    '💩': 'Pile of poo',
    '🤡': 'Clown face',
    '👻': 'Ghost',
    '👽': 'Alien',
    '👾': 'Alien monster',
    '🤖': 'Robot',
    '❤️': 'Red heart',
    '🧡': 'Orange heart',
    '💛': 'Yellow heart',
    '💚': 'Green heart',
    '💙': 'Blue heart',
    '💜': 'Purple heart',
    '🖤': 'Black heart',
    '🤍': 'White heart',
    '🤎': 'Brown heart',
    '💔': 'Broken heart',
    '❣️': 'Exclamation heart',
    '💕': 'Two hearts',
    '💞': 'Revolving hearts',
    '💓': 'Beating heart',
    '💗': 'Growing heart',
    '💖': 'Sparkling heart',
    '💘': 'Heart with arrow',
    '💝': 'Heart with ribbon',
    '💟': 'Heart decoration',
    '💌': 'Love letter',
    '💋': 'Kiss mark',
    '💍': 'Ring',
    '💎': 'Gem stone',
    '🌹': 'Rose',
    '🥀': 'Wilted flower',
    '💐': 'Bouquet',
    '🏳️': 'White flag',
    '🏴': 'Black flag',
    '🏁': 'Chequered flag',
    '🚩': 'Triangular flag',
  };
  
  return labels[emoji] || `Emoji: ${emoji}`;
};


const selectEmoji = (emoji: string) => {
  emit('select', emoji);
  if (!props.inline) {
    emit('close');
  }
};

const handleClickOutside = () => {
  emit('close');
};

onActivated(() => {
  nextTick(() => scrollToCategory(activeCategory.value, 'auto'));
});

onMounted(() => {
  if (!props.inline) {
    document.addEventListener('click', handleClickOutside);
  }

  nextTick(() => {
    updateActiveFromScroll();
    scrollActiveTabIntoView('auto');
  });
});

onUnmounted(() => {
  if (!props.inline) {
    document.removeEventListener('click', handleClickOutside);
  }
});
</script>

<style scoped>
.emoji-picker {
  background-color: var(--bs-body-bg, #fff);
  border: 1px solid var(--bs-border-color, #dee2e6);
  border-radius: var(--bs-border-radius, 0.375rem);
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.emoji-picker.emoji-picker--inline {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: transparent;
  border: none;
  box-shadow: none;
  --hover-bg: #f8f9fa;
}

.emoji-tabs {
  display: flex;
  gap: 4px;
  padding-left: 6px;
  padding-right: 6px;
  padding-top: 6px;
  padding-bottom: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  justify-content: flex-start;
  overflow-x: auto;
  overflow-y: hidden;
  flex-wrap: nowrap;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.emoji-tabs::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.emoji-tab {
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 18px;
  padding: 0;
}

.emoji-tab:focus,
.emoji-tab:focus-visible {
  box-shadow: none;
}


.emoji-tab :deep(.fa) {
  color: inherit;
}

.emoji-items {
  flex: 1;
  display: block;
  padding-bottom: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  user-select: none;
  -webkit-user-select: none;
}

@media (max-width: 767.98px) {
  .emoji-picker {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  
  .emoji-items {
    flex: 1;
    padding-bottom: 160px;
    max-height: 320px !important;
  }
  
  .emoji-tabs {
    margin-top: auto;
    flex-shrink: 0;
  }
}

.emoji-category {
  padding-bottom: 8px;
}

.emoji-anchor {
  height: 0;
  width: 0;
}

.emoji-category-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 32px);
  justify-content: center;
  gap: 4px;
}

.emoji-items::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.emoji-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem !important;
  height: 2rem !important;
  font-size: 20px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.15s ease;
  padding: 0 !important;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.emoji-item:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

.emoji-item:active {
  background-color: rgba(0, 0, 0, 0.12);
  transform: scale(1.15);
}
</style>
